/**
 * Email Service
 * =============
 * Handles sending transactional emails via the send-email Edge Function.
 */

import { callEdgeFunction } from '../config/edge-functions.config'
import { supabase } from '../lib/supabase'

// Template types matching the backend Edge Function
export type EmailTemplate =
  | 'welcome'
  | 'assignment_new'
  | 'assignment_completed'
  | 'subscription_confirmed'
  | 'password_reset'
  | 'client_access_link'
  | 'video_ready'
  | 'team_invitation'

export interface SendEmailResponse {
  success: boolean
  emailId?: string
  error?: string
}

export interface WelcomeEmailData {
  name: string
  [key: string]: unknown
}

export interface AssignmentNewEmailData {
  coachName: string
  playlistName: string
  exerciseCount: number
  assignmentId: string
  accessLink: string
  [key: string]: unknown
}

export interface AssignmentCompletedEmailData {
  clientName: string
  playlistName: string
  [key: string]: unknown
}

export interface SubscriptionConfirmedEmailData {
  tier: string
  [key: string]: unknown
}

export interface ClientAccessLinkEmailData {
  clientName: string
  providerName: string
  accessLink: string
  expiresAt?: string
  [key: string]: unknown
}

export interface VideoReadyEmailData {
  videoTitle: string
  videoId: string
  [key: string]: unknown
}

export interface TeamInvitationEmailData {
  inviterName: string
  organizationName: string
  role: string
  inviteLink: string
  [key: string]: unknown
}

export const emailService = {
  /**
   * Send a welcome email to a new user
   */
  async sendWelcomeEmail(
    to: string,
    data: WelcomeEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'welcome', data)
  },

  /**
   * Send assignment notification to a client
   */
  async sendAssignmentNotification(
    to: string,
    data: AssignmentNewEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'assignment_new', data)
  },

  /**
   * Send assignment completed notification to provider
   */
  async sendAssignmentCompleted(
    to: string,
    data: AssignmentCompletedEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'assignment_completed', data)
  },

  /**
   * Send subscription confirmation email
   */
  async sendSubscriptionConfirmed(
    to: string,
    data: SubscriptionConfirmedEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'subscription_confirmed', data)
  },

  /**
   * Send client access link email
   */
  async sendClientAccessLink(
    to: string,
    data: ClientAccessLinkEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'client_access_link', data)
  },

  /**
   * Send video ready notification
   */
  async sendVideoReady(
    to: string,
    data: VideoReadyEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'video_ready', data)
  },

  /**
   * Send team invitation email
   */
  async sendTeamInvitation(
    to: string,
    data: TeamInvitationEmailData
  ): Promise<SendEmailResponse> {
    return this.sendEmail(to, 'team_invitation', data)
  },

  /**
   * Generic email sending method
   */
  async sendEmail<T extends Record<string, unknown>>(
    to: string,
    template: EmailTemplate,
    data: T
  ): Promise<SendEmailResponse> {
    try {
      // Get current session token for auth
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        console.error('No auth session found for sending email')
        return {
          success: false,
          error: 'Not authenticated',
        }
      }

      const response = await callEdgeFunction<SendEmailResponse>('send-email', {
        body: { to, template, data },
        token,
      })
      return response
    } catch (error) {
      console.error(`Failed to send ${template} email:`, error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email',
      }
    }
  },
}
